/**
 * Built-in Tool Provider
 *
 * Provides tools that are built into the ARCHON platform.
 * These tools are fetched from the backend API.
 */

import { BaseToolProvider } from './base'
import type { Tool, ToolProviderType } from '../types'
import api from '@/services/api'

// Tool response from backend (matches app/schemas/tool.py ToolResponse)
interface ToolResponse {
  id: string
  organization_id: string
  name: string
  description: string | null
  tool_type: string
  category: string
  config: Record<string, unknown>
  is_active: boolean
  is_configured: boolean
  agent_count: number
  created_at: string
  updated_at: string
}

interface ToolListResponse {
  tools: ToolResponse[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Tool-specific icon mapping (config.icon -> emoji)
const TOOL_ICONS: Record<string, string> = {
  'google-drive': '📁',
  'github': '🐙',
  'slack': '💬',
  'search': '🔍',
  'code': '💻',
  'python': '🐍',
  'email': '📧',
  'database': '🗄️',
  'api': '🔌',
  'file': '📄',
  'calendar': '📅',
  'notion': '📝',
  'jira': '📋',
  'confluence': '📚',
  'teams': '👥',
  'discord': '🎮',
  'telegram': '✈️',
  'whatsapp': '📱',
}

// Category icons mapping
const CATEGORY_ICONS: Record<string, string> = {
  web: '🌐',
  data: '📊',
  communication: '💬',
  file: '📁',
  search: '🔍',
  code: '💻',
  ai: '🤖',
  productivity: '📋',
  development: '⚙️',
  custom: '🔧',
  default: '🔧',
}

// Category labels mapping (tool_type -> category fallback)
const CATEGORY_LABELS: Record<string, string> = {
  web_search: 'search',
  api_call: 'web',
  database: 'data',
  file_reader: 'file',
  file_writer: 'file',
  email: 'communication',
  slack: 'communication',
  code_executor: 'code',
  llm: 'ai',
  internal: 'productivity',
  mcp: 'development',
  api: 'web',
}

export class BuiltinToolProvider extends BaseToolProvider {
  readonly name: ToolProviderType = 'builtin'
  readonly displayName = 'Built-in Tools'
  readonly description = 'Ferramentas integradas ao ARCHON'

  private organizationId: string | null = null

  setOrganizationId(organizationId: string): void {
    this.organizationId = organizationId
    // Reset tools when organization changes
    this.tools = []
    this.initialized = false
  }

  /**
   * @deprecated Use setOrganizationId instead
   */
  setProjectId(projectId: string): void {
    console.warn('setProjectId is deprecated, use setOrganizationId instead')
    // Keep for backwards compatibility but do nothing
    void projectId
  }

  protected async loadTools(): Promise<void> {
    try {
      // If no organization ID, we can't fetch tools
      if (!this.organizationId) {
        this.tools = []
        return
      }

      const response = await api.get<ToolListResponse>(
        `/organizations/${this.organizationId}/tools`,
        { params: { page_size: 100 } }
      )

      this.tools = response.data.tools.map((t) => this.mapToolResponse(t))
    } catch {
      // If tools endpoint is not available yet, use empty list
      console.warn('Tools endpoint not available, using empty list')
      this.tools = []
    }
  }

  private mapToolResponse(tool: ToolResponse): Tool {
    // Use category from backend, fallback to mapping by tool_type
    const category = tool.category || CATEGORY_LABELS[tool.tool_type] || 'default'

    // Get icon: first check tool-specific mapping, then category, then default
    const configIcon = tool.config?.icon as string | undefined
    const icon = configIcon
      ? TOOL_ICONS[configIcon] || CATEGORY_ICONS[category] || CATEGORY_ICONS.default
      : CATEGORY_ICONS[category] || CATEGORY_ICONS.default

    return {
      id: tool.id,
      name: tool.name,
      description: tool.description ?? '',
      icon,
      category,
      provider: 'builtin',
      enabled: tool.is_active,
      configSchema: tool.config?.parameters_schema
        ? {
            type: 'object',
            properties: tool.config.parameters_schema as Record<
              string,
              { type: 'string' | 'number' | 'boolean' | 'array' | 'object' }
            >,
          }
        : undefined,
    }
  }

  /**
   * Get predefined/demo tools when backend is not available
   * This is useful for development and testing
   */
  static getDemoTools(): Tool[] {
    return [
      {
        id: 'demo-web-search',
        name: 'Web Search',
        description: 'Pesquisa na web usando Google, Bing ou DuckDuckGo',
        icon: '🔍',
        category: 'web',
        provider: 'builtin',
        enabled: true,
        configSchema: {
          type: 'object',
          properties: {
            engine: {
              type: 'string',
              title: 'Search Engine',
              enum: ['google', 'bing', 'duckduckgo'],
              default: 'google',
            },
            max_results: {
              type: 'number',
              title: 'Max Results',
              minimum: 1,
              maximum: 20,
              default: 10,
            },
          },
        },
        defaultConfig: {
          engine: 'google',
          max_results: 10,
        },
      },
      {
        id: 'demo-http-request',
        name: 'HTTP Request',
        description: 'Faz requisições HTTP para APIs externas',
        icon: '🌐',
        category: 'web',
        provider: 'builtin',
        enabled: true,
        configSchema: {
          type: 'object',
          properties: {
            timeout: {
              type: 'number',
              title: 'Timeout (seconds)',
              minimum: 1,
              maximum: 120,
              default: 30,
            },
          },
        },
        defaultConfig: {
          timeout: 30,
        },
      },
      {
        id: 'demo-calculator',
        name: 'Calculator',
        description: 'Realiza cálculos matemáticos',
        icon: '🔢',
        category: 'data',
        provider: 'builtin',
        enabled: true,
      },
      {
        id: 'demo-code-interpreter',
        name: 'Code Interpreter',
        description: 'Executa código Python em ambiente sandbox',
        icon: '💻',
        category: 'code',
        provider: 'builtin',
        enabled: true,
        configSchema: {
          type: 'object',
          properties: {
            timeout: {
              type: 'number',
              title: 'Execution Timeout (seconds)',
              minimum: 1,
              maximum: 300,
              default: 60,
            },
            memory_limit: {
              type: 'number',
              title: 'Memory Limit (MB)',
              minimum: 64,
              maximum: 2048,
              default: 512,
            },
          },
        },
        defaultConfig: {
          timeout: 60,
          memory_limit: 512,
        },
      },
      {
        id: 'demo-file-reader',
        name: 'File Reader',
        description: 'Lê conteúdo de arquivos (PDF, DOCX, TXT, etc.)',
        icon: '📖',
        category: 'file',
        provider: 'builtin',
        enabled: true,
      },
      {
        id: 'demo-email-sender',
        name: 'Email Sender',
        description: 'Envia emails através de SMTP ou serviços como SendGrid',
        icon: '📧',
        category: 'communication',
        provider: 'builtin',
        enabled: true,
        configSchema: {
          type: 'object',
          properties: {
            from_name: {
              type: 'string',
              title: 'From Name',
            },
            from_email: {
              type: 'string',
              title: 'From Email',
            },
          },
        },
      },
    ]
  }
}

// Singleton instance
export const builtinToolProvider = new BuiltinToolProvider()
